<template>
  <div v-if="guid && !dragging">
    <div class="loader" v-if="loading">
      <q-spinner-gears color="grey-3" :size="80" />
    </div>
    <div :class="loading ? 'hidden': ''">
      <editor
        :disabled="disabled"
        tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
        :init="tinyConfig()"
        :initial-value="savedState"
        @input="broadcast()"
        @onInit="loaded()"
        ref="tm"
        v-model="editorContent">
      </editor>
      </div>
    <q-modal v-if="fileModal" v-model="fileModal">
      <file-select-modal :fileType="'image'" :guid="guid"/>
    </q-modal>

    <!-- Button style modal -->
    <q-modal v-model="buttonModal">
          <q-modal-layout>
            <q-toolbar slot="header" color="tertiary">
              <q-toolbar-title>
                  {{ $store.state.stringsStore.language === 'nl' ? 'Selecteer een stijl' : 'Select button style' }}
              </q-toolbar-title>
              <q-btn
                  flat
                  round
                  dense
                  v-close-overlay
                  icon="close"
              />
            </q-toolbar>

            <div class="q-pa-md button-select">
              <button @click="selectButtonStyle('red')" class="editor-button editor-button__red">{{ buttonText || 'Red' }}</button>
              <button @click="selectButtonStyle('black')" class="editor-button editor-button__black">{{ buttonText || 'Black' }}</button>
              <button @click="selectButtonStyle('grey')" class="editor-button editor-button__grey">{{ buttonText || 'Grey' }}</button>
              <button @click="selectButtonStyle('lightgrey')" class="editor-button editor-button__lightgrey">{{ buttonText || 'Lightgrey' }}</button>
              <button @click="selectButtonStyle('white')" class="editor-button editor-button__white">{{ buttonText || 'White' }}</button>
            </div>
          </q-modal-layout>
    </q-modal>
  </div>
</template>

<script>
import EventBus from '@/event-bus';
import _debounce from 'lodash.debounce';
import createUuid from '@/helpers/createUuid';
import FileSelectModal from '@/components/modal/FileSelectModal.vue';

import libraryCommon from '@/mixins/library/common';

const Editor = require('@tinymce/tinymce-vue').default;

export default {
  props: {
    linkList: {
      type: Array,
      default: null,
    },
    source: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    savedState: { // Previous state of the editor if the parent content block has been minimized.
      type: String,
    },
  },

  name: 'richTextEditor',

  components: {
    FileSelectModal,
    Editor,
  },

  mixins: [
    libraryCommon,
  ],

  data() {
    return {
      editor: null,
      buttonModal: false,
      textSelection: '',
      loading: true,
      dragging: false,
      fileModal: false,
      editorContent: '',
      plugins: [
        'wordcount',
        'code',
        'link',
        'lists',
        'anchor',
        'template',
        'media',
        'importcss',
        'table',
        'paste',
      ],
      guid: null,
    };
  },

  watch: {
    linkList() {
      if (!this.editor) {
        return;
      }
      // Manually update the link list
      this.editor.settings.link_list = this.linkList;
    },
  },

  methods: {
    selectButtonStyle(style) {
      if (!this.editor) {
        return;
      }

      this.editor.focus();

      const text = this.editor.selection.getContent({ format: 'html' });

      if (!text || text.length === 0) {
        this.buttonModal = false;
        return;
      }

      if (text && text.length > 0) {
        this.editor.execCommand('mceInsertContent', false, `<button class="editor-button editor-button__${style}">${text}</button>`);
      }

      this.buttonModal = !this.buttonModal;

      // Broadcast the change
      this.broadcast();
    },

    loaded() {
      this.loading = false;
    },

    insertImage(editor) {
      EventBus.$on(`file-select:${this.guid}`, (data) => {
        // Image selected, remove the event listener, close the modal.
        EventBus.$off(`file-select:${this.guid}`);
        this.fileModal = false;

        const range = editor.selection.getRng();
        const newNode = editor.getDoc().createElement('img'); // create img node in the editor
        newNode.src = this.buildFileLocation(data.location); // add src attribute

        if (data.alt) {
          newNode.alt = data.alt; // Conditionally include the meta tag for the image (read from library)
        }

        range.insertNode(newNode);

        // Broadcast the change
        this.broadcast();
      });
      this.fileModal = true;
    },

    insertScript(editor, data, edit = false) {
      // Check if we are editing (the old node will be replaced)
      if (edit) {
        const sel = editor.selection.getNode();
        // Remove the old element
        sel.parentNode.removeChild(sel);
      }

      const range = editor.selection.getRng();

      const wrapperNode = editor.getDoc().createElement('div'); // create div node in the editor
      wrapperNode.className = 'whitelist-js';

      let external = true;

      // Check if the entered text is a valid URL
      try {
        // eslint-disable-next-line no-new
        new URL(data.code);
      } catch (_) {
        external = false;
      }

      const hasScript = str => Boolean(
        new DOMParser().parseFromString(str, 'text/html').querySelector('script'),
      );

      if (hasScript(data.code)) {
        wrapperNode.innerHTML = data.code;
      } else if (external) {
        // eslint-disable-next-line no-useless-escape
        wrapperNode.innerHTML = `<script type="application/javascript" src="${data.code}"><\/script>`;
      } else {
        // eslint-disable-next-line no-useless-escape
        wrapperNode.innerHTML = `<script type="application/javascript">${data.code}<\/script>`;
      }

      // Insert the new or updated script element
      range.insertNode(wrapperNode);

      // Broadcast the change
      this.broadcast();
    },

    insertButton(editor) {
      this.textSelection = editor.selection.getContent({ format: 'text' });
      if (this.textSelection && this.textSelection.length > 0) {
        this.buttonModal = true;
      }
    },

    openScriptManager(editor) {
      // Open window
      editor.windowManager.open({
        title: 'Add script',
        width: 440,
        height: 240,
        body: [
          {
            placeholder: this.$store.state.stringsStore.language === 'nl'
              ? 'Voeg een script toe (of een URL voor een extern script)'
              : 'Enter a script (or the URL of an external script)',
            type: 'textbox',
            multiline: true,
            minHeight: 200,
            name: 'code',
          },
        ],

        onsubmit: (e) => {
          this.insertScript(editor, e.data);
        },
      });
    },

    broadcast: _debounce(function () {
      this.$emit('input', this.editorContent);
      // Publish changes to the content block. (specific to this instance)
      EventBus.$emit(`tiny-editor:${this.source.guid}`, { source: this.source, content: this.editorContent });
    }, 250),

    tinyConfig() {
      return {
        height: 450,
        branding: false,
        browser_spellcheck: true,
        plugins: this.plugins,
        extended_valid_elements: [
          'script[charset|defer|language|src|type]',
        ],
        toolbar: [
          'undo redo | formatselect | bullist numlist checkmarkList | bold italic | link image alignleft aligncenter alignright | imageSelect | addScript | addButton | media | code | anchor | table',
        ],
        templates: [
          { title: 'Name', description: 'Users full name', content: `${this.$store.state.meStore.me.name.firstname} ${this.$store.state.meStore.me.name.surname}` },
        ],
        paste_as_text: true,

        link_list: this.$store.state.editorStore.blockIds,

        content_css: '/editor.css',

        setup: (editor) => {
          // Store a reference to the editor object
          this.editor = editor;

          editor.addButton('imageSelect', {
            tooltip: 'Select an image from the library',
            icon: 'books',
            onclick: () => {
              this.insertImage(editor);
            },
          });

          editor.addButton('addButton', {
            text: 'button',
            icon: false,
            onclick: () => {
              this.insertButton(editor);
            },
          });

          editor.on('click', (e) => {
            e = e.target;

            if (e.nodeName === 'DIV' && e.classList && e.classList.contains('whitelist-js')) {
              editor.selection.select(e);

              // Open window
              editor.windowManager.open({
                title: 'Add script',
                width: 440,
                height: 240,
                body: [
                  {
                    placeholder: this.$store.state.stringsStore.language === 'nl'
                      ? 'Voeg een script toe (of een URL voor een extern script)'
                      : 'Enter a script (or the URL of an external script)',
                    type: 'textbox',
                    value: e.firstElementChild.innerHTML,
                    multiline: true,
                    minHeight: 200,
                    name: 'code',
                  },
                ],

                onsubmit: (ev) => {
                  this.insertScript(editor, ev.data, true);
                },
              });
            }
          });

          editor.addButton('addScript', {
            text: '<script>',
            icon: false,
            stateSelector: 'whitelist-js',
            onPostRender() {
              const ctrl = this;

              editor.on('NodeChange', (e) => {
                ctrl.active(e.element.className === 'whitelist-js');
              });
            },
            onclick: () => {
              this.openScriptManager(editor);
            },
          });

          editor.addButton('checkmarkList', {
            icon: 'checkmark',
            tooltip: 'Toggle Checkmark List',

            onPostRender() {
              const self = this;

              // Function to check if the current node or any parent is a list with the 'checkmark' class
              const isCheckmarkList = (node) => {
                while (node) {
                  if ((node.nodeName === 'UL' || node.nodeName === 'OL') && node.classList.contains('checkmark')) {
                    return true;
                  }
                  node = node.parentNode;
                }
                return false;
              };

              // Set the initial state of the button
              editor.on('NodeChange', () => {
                const selectedNode = editor.selection.getNode();

                if (isCheckmarkList(selectedNode)) {
                  self.active(true); // Highlight the button
                } else {
                  self.active(false); // Unhighlight the button
                }
              });
            },
            onclick() {
              const selectedNode = editor.selection.getNode();
              const listNode = selectedNode.closest('ul, ol');

              // Check if the list exists and toggle the 'checkmark' class
              if (listNode) {

                listNode.classList.toggle('checkmark');
              } else {
                // If the selection is not a list, insert a new UL with the 'checkmark' class
                editor.execCommand('InsertUnorderedList');

                // set a timeout to ensure the list is created before we try to find it
                setTimeout(() => {
                  const newList = editor.selection.getNode().closest('ul, ol');

                  if (newList.nodeName === 'UL') {
                    newList.classList.add('checkmark');
                  }
                  editor.nodeChanged();
                }, 100);
              }

              // Manually trigger NodeChange to update button state
                editor.nodeChanged();
            },
          });
        },
        pagebreak_split_block: true,
      };
    },
  },

  computed: {
    buttonText() {
      const formatted = this.textSelection.length > 12 ? `${this.textSelection.substring(0, 9)}...` : this.textSelection;
      return this.textSelection ? formatted : null;
    },
  },

  mounted() {
    EventBus.$on('file-select-close', () => {
      this.imageModal = false;
      this.fileModal = false;
    });
    // Create a unique Id to listen to incoming events for this instance of the image selector.
    this.guid = createUuid();

    // Make sure this component rerenders after dragging content blocks.
    EventBus.$on('content-editor:dragging', (dragging) => {
      this.$nextTick(() => {
        this.dragging = dragging;
      });
    });
  },

  destroyed() {
    EventBus.$off('content-editor:dragging');
    EventBus.$off('file-select-close');
  },
};
</script>

<style lang="stylus">
.hidden
  display none
.loader
  display flex
  justify-content center

.editor-button {
    border: none;
    font-weight: 600;
    box-sizing: border-box;
    max-height: 43px;
    box-shadow: 0 0 4.5px 0 rgba(0, 0, 0, 0.20);
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: 5px;
    padding: 0px 20px;
    line-height: 2.6em;
    font-size: 1em;
}
.editor-button__red {
    background-color: rgb(226, 0, 26);
    color: white;
}
.editor-button__white {
    background-color: white;
    color: black;
}
.editor-button__black {
    background-color: black;
    color: white;
}
.editor-button__grey {
    background-color: rgb(118, 118, 118);
    color: white;
}
.editor-button__lightgrey {
    color: #767676;
    background-color: #f2f2f2;
}
.button-select
  max-width 350px
  display flex
  flex-wrap wrap
  & *
    margin 12px
</style>
